import {
  money,
  sku,
  type ProductDetail,
  type ProductFilters,
  type ProductImage,
  type ProductListPage,
  type ProductSummary,
} from '@sshop/domain';

import type {
  ProductDetailDto,
  ProductFiltersDto,
  ProductListDto,
  ProductSummaryDto,
} from './product.dto';

function toImage(dto: NonNullable<ProductSummaryDto['image']>): ProductImage {
  return {
    url: dto.url,
    alt: dto.alt,
    ...(dto.width !== undefined ? { width: dto.width } : {}),
    ...(dto.height !== undefined ? { height: dto.height } : {}),
  };
}

export function toProductSummary(dto: ProductSummaryDto): ProductSummary {
  return {
    id: dto.id,
    sku: sku(dto.sku),
    ...(dto.meta_catalog ? { metaCatalog: dto.meta_catalog } : {}),
    slug: dto.slug,
    title: dto.title,
    brand: dto.brand,
    facts: dto.facts,
    categorySlug: dto.category_slug,
    price: money(dto.price.amount, dto.price.currency),
    compareAtPrice: dto.compare_at_price
      ? money(dto.compare_at_price.amount, dto.compare_at_price.currency)
      : null,
    image: dto.image ? toImage(dto.image) : null,
    inStock: dto.in_stock,
    rating: dto.rating,
    reviewCount: dto.review_count,
    offer: dto.offer ? {
      id: dto.offer.id,
      title: dto.offer.title,
      description: dto.offer.description,
      type: dto.offer.type,
      requiredQuantity: dto.offer.required_quantity,
      discountType: dto.offer.discount_type,
      discountValue: dto.offer.discount_value,
      endsAt: dto.offer.ends_at,
    } : null,
    ...(dto.promotion_ids !== undefined ? { promotionIds: dto.promotion_ids } : {}),
  };
}

export function toProductDetail(dto: ProductDetailDto): ProductDetail {
  return {
    ...toProductSummary(dto),
    metaTitle: dto.meta_title,
    description: dto.description,
    images: dto.images.map(toImage),
    specs: dto.specs,
    variants: dto.variants.map((variant) => ({
      id: variant.id,
      slug: variant.slug,
      label: variant.label,
      inStock: variant.in_stock,
    })),
    colorVariations: dto.color_variations.map((variation) => ({
      id: variation.id,
      slug: variation.slug,
      label: variation.label,
      color: variation.color,
      inStock: variation.in_stock,
    })),
    stockCount: dto.stock_count,
    categories: dto.categories.map((c) => ({ id: c.id, slug: c.slug, name: c.name })),
  };
}

export function toProductListPage(dto: ProductListDto): ProductListPage {
  return {
    items: dto.data.map(toProductSummary),
    page: dto.meta.current_page,
    pageSize: dto.meta.per_page,
    totalItems: dto.meta.total,
    totalPages: dto.meta.last_page,
    ...(dto.meta.promotion_filter_id !== undefined ? { promotionFilterId: dto.meta.promotion_filter_id } : {}),
  };
}

export function toProductFilters(dto: ProductFiltersDto): ProductFilters {
  return {
    priceMin: dto.data.price_range.min,
    priceMax: dto.data.price_range.max,
    attributes: dto.data.attributes.map((attribute) => ({
      id: attribute.id,
      name: attribute.name,
      code: attribute.code,
      type: attribute.type,
      options: attribute.options,
    })),
    categories: dto.data.categories,
  };
}
